<template>
  <div
    class="listing"
    :class="{
      ordered: !!ordered,
      cancelled: ordered && basketEntry && basketEntry.isReturned,
      basket: !ordered && basketEntry,
      reserved: !!reserved,
      error: hasError,
      disabled: disabled,
    }"
  >
    <div>
      <div
        v-for="error in errors"
        :key="error.error"
        class="error-info"
      >
        {{ error.error ? error.error : error.warning }}
      </div>
    </div>
    <div
      v-if="reserved"
      class="d-flex align-items-center"
    >
      <div class="reserved-info">
        <i class="far fa-clock mr-1" />
        {{ $t('shop.reserveQueue') }}
        {{ $t('shop.position') }}: {{ reserved.position + 1 }}
      </div>
    </div>
    <div
      class="d-flex align-items-center"
    >
      <div v-if="listing.photoUrl">
        <img
          class="photo"
          :src="listing.photoUrl"
        >
      </div>
      <div class="w-100">
        <div class="listing-name">
          {{ listing.name }}
        </div>
        <div class="d-flex justify-content-between w-100">
          <div class="money-font">
            {{ formatCurrency(listing.price, listing.currency) }}
          </div>

          <div class="listing-icon">
            <span
              v-if="totalCount > 1"
              class="small text-primary"
            >
              {{ totalCount }}
            </span>
            <i
              v-if="ordered && basketEntry && basketEntry.isReturned"
              class="fas fa-ban text-primary"
            />
            <i
              v-else-if="ordered && !basketEntry"
              class="fas fa-circle-check text-primary"
            />
            <i
              v-else-if="basketEntry"
              class="fas fa-cart-shopping text-primary"
            />
            <i
              v-else-if="hasError"
              class="fas fa-ban"
            />
            <i
              v-else-if="countLimitExceeded"
              class="far fa-clock"
            />
            <i
              v-else
              class="fas fa-plus"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

export const formatDate = (date) => {
  const m = moment(date, 'YYYY-MM-DD HH:mm');
  if (m.year() === moment().year()) {
    return m.format('DD MMMM HH:mm');
  }

  return m.format('DD MMMM YYYY HH:mm');
};

export default {
  props: {
    listing: Object,
    reserved: Object,
    payerId: String,
    ordered: Object,
    disabled: Boolean,
  },
  data: () => ({
    now: moment(),
  }),
  computed: {
    ...mapGetters([
      'formatCurrency',
      'catalogBasket',
    ]),
    hasError() {
      return this.errors.filter((x) => x.error).length > 0;
    },
    countLimitExceeded() {
      return this.listing.countLimit >= 0 && this.listing.countBlocked >= this.listing.countLimit;
    },
    totalCount() {
      const basketCount = this.basketEntry?.isReturned
        ? -this.basketEntry?.count
        : this.basketEntry?.count || 0;

      return (this.ordered?.count || 0) + basketCount;
    },
    errors() {
      if (this.reserved) {
        return [];
      }

      const canCancel = !this.listing.cancelTo || moment(this.listing.cancelTo).isAfter(this.now);
      if (this.ordered) {
        if (!this.listing.cancelTo) {
          return [];
        }
        if (!canCancel) {
          return [
            {
              error: `${this.$t('shop.cancellationExpired')}`,
            },
          ];
        }

        return [
          {
            warning: `${this.$t('shop.cancelTo')}: ${moment(this.listing.cancelTo).format('DD MMM HH:mm')}`,
          },
        ];
      }

      const canOrderFrom = !this.listing.orderFrom
          || moment(this.listing.orderFrom, 'YYYY-MM-DD HH:mm').isBefore(this.now);
      const canOrderTo = !this.listing.orderTo
          || moment(this.listing.orderTo, 'YYYY-MM-DD HH:mm').isAfter(this.now);

      const errors = [];
      if (!canOrderFrom) {
        errors.push({
          error: `${this.$t('shop.orderAvailableFrom')}: ${formatDate(this.listing.orderFrom)}`,
        });
      }

      if (!canOrderTo && canOrderFrom) {
        errors.push({
          error: `${this.$t('shop.orderAvailableTo')}: ${formatDate(this.listing.orderTo)}`,
        });
      }

      if (this.countLimitExceeded && canOrderFrom && canOrderTo) {
        errors.push({
          warning: `${this.$t('shop.noSpotsAvailable')}`,
        });
      }

      return errors;
    },
    basketEntry() {
      return this.catalogBasket
        .find((x) => x.payerId === this.payerId && x.offerId === this.listing.id);
    },
  },
  created() {
    this.now = moment();

    this.nowRefresh = setInterval(() => {
      this.now = moment();
    }, 1000);
  },
  destroyed() {
    if (this.nowRefresh) {
      clearInterval(this.nowRefresh);
    }
  },
};
</script>

<style lang="scss" scoped>
.listing {
  background-color: white;
  border-radius: 15px;
  padding: 10px 12px;
  font-size: 0.9rem;
  border: 1px solid transparent;
  cursor: pointer;

  &.ordered {
    background-color: #D9E9FF;
  }

  &.cancelled {
    .listing-name {
      text-decoration: line-through;
    }
  }

  &.error, &.disabled {
    opacity: 0.6;
  }

  &.basket {
    border: 1px dashed #007bff;
  }

  &.reserved {
    background-color: lighten(#FFD28D, 15);
  }

  &:hover, &:active {
    border: 1px solid #aaa;
  }
}

.error-info {
  color: "#aaa";
  font-size: 0.7rem;
  margin-bottom: 2px;
}

.reserved-info {
  font-size: 0.7rem;
}

.photo {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  margin-right: 14px;
}

.listing-icon {
  font-size: 18px;
}

.listing-name {
  font-weight: 500;
}
</style>
